/**
 * widgets/registry.phone.tsx — zjednodušené phone plochy (ACM design system).
 *
 * Otevírají se ze Start dlaždic (/phone/:slug). Layout/ergonomie = Metro
 * (zpět, app bar); vzhled = stejné sklo/plex/Badge jako zbytek ACM.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  LayoutDashboard,
  Network,
  RefreshCw,
  Server,
} from 'lucide-react';
import { CodableIcon } from '../components/icons/CodableIcon';
import { api } from '../lib/api';
import { Badge, Button, StatusDot } from '../components/ui';
import { NodePicker, useMeshPeers, useNodeDisplayLabel } from './NodePicker';
import type { WidgetPanel, WidgetRenderProps, WidgetSpec } from './types';

function PhoneDashboardBody(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: peers } = useMeshPeers({ includeLocal: true });
  const list = Array.isArray(peers) ? peers : [];
  const online = list.filter((p) => p.online === true || p.status === 'online').length;

  const sys = useQuery({
    queryKey: ['phone-dash-sys'],
    queryFn: () =>
      api.mcp<{
        hostname?: string;
        cpu_percent?: number;
        mem_percent?: number;
        disk_percent?: number;
      }>('system-settings.system_info', {}).catch(() => ({})),
    refetchInterval: 30_000,
  });

  const health = useQuery({
    queryKey: ['phone-dash-health'],
    queryFn: () =>
      api.mcp<{ ok?: boolean; status?: string }>('toolbox.health_status', {}).catch(() => ({
        ok: false,
        status: '?',
      })),
    refetchInterval: 60_000,
  });

  const info = sys.data ?? {};
  const host = String(info.hostname ?? 'ACM');
  const healthOk = health.data?.ok === true;

  return (
    <div className='phone-metro-stack'>
      <div className='phone-metro-hero'>
        <div className='phone-metro-hero-top'>
          <span className='phone-metro-hero-title'>{host}</span>
          <Badge variant={healthOk ? 'green' : 'red'}>
            {healthOk ? 'OK' : (health.data?.status ?? '…')}
          </Badge>
        </div>
        <div className='phone-metro-hero-sub'>
          {t('phone.dashboard.subtitle', 'Rychlý stav')}
        </div>
      </div>

      <div className='phone-metro-stats'>
        <div className='phone-metro-stat'>
          <span className='phone-metro-stat-label'>CPU</span>
          <span className='phone-metro-stat-value'>{fmtPct(info.cpu_percent)}</span>
        </div>
        <div className='phone-metro-stat'>
          <span className='phone-metro-stat-label'>RAM</span>
          <span className='phone-metro-stat-value'>{fmtPct(info.mem_percent)}</span>
        </div>
        <div className='phone-metro-stat'>
          <span className='phone-metro-stat-label'>Disk</span>
          <span className='phone-metro-stat-value'>{fmtPct(info.disk_percent)}</span>
        </div>
      </div>

      <button
        type='button'
        className='phone-metro-row'
        onClick={() => navigate('/core/mesh')}
      >
        <Network size={18} strokeWidth={1.75} />
        <div className='phone-metro-row-text'>
          <span className='phone-metro-row-title'>{t('phone.dashboard.mesh', 'Mesh nody')}</span>
          <span className='phone-metro-row-sub'>{online}/{list.length} online</span>
        </div>
        <ChevronRight size={16} className='text-gray-600 shrink-0' />
      </button>

      <div className='phone-metro-actions'>
        <Button variant='secondary' size='sm' className='w-full' onClick={() => navigate('/core/dashboard')}>
          {t('phone.dashboard.full', 'Plný dashboard')}
        </Button>
        <Button variant='ghost' size='sm' className='w-full' onClick={() => navigate('/core/tasks')}>
          {t('nav.tasks', 'Tasks')}
        </Button>
      </div>
    </div>
  );
}

function fmtPct(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return `${Math.round(n)}%`;
}

/** Codable hub — carusel (Projekty · Nový · Knihovna · Šablony · Nastavení). */
function PhoneCodableBody(_props: WidgetRenderProps) {
  // Thin wrapper — stejná stránka jako route /phone/codable
  const Page = require('../pages/PhoneCodable').default as typeof import('../pages/PhoneCodable').default;
  return <Page />;
}

function PhoneNodeBody({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [node, setNode] = useStateNode(scope.node ?? null);

  const statusQ = useQuery({
    queryKey: ['phone-node-status', node ?? 'local'],
    queryFn: async () => {
      if (!node) {
        return api.mcp<{
          online?: boolean;
          node_id?: string;
          role?: string;
          health?: { ok?: boolean; status?: string };
        }>('mesh.local_status', {}, { silent: true });
      }
      // Background poll — bez toastu (offline peer / timeout je stav, ne chyba uživatele).
      return api.mcp<{
        online?: boolean;
        node?: { name?: string; host?: string };
        health?: { ok?: boolean; status?: string };
      }>('mesh.peers_status', { name: node }, { silent: true });
    },
    refetchInterval: 20_000,
    retry: 1,
  });

  const refreshMut = useMutation({
    mutationFn: async () => {
      await qc.invalidateQueries({ queryKey: ['phone-node-status'] });
      await qc.invalidateQueries({ queryKey: ['mesh-peers-list'] });
      await statusQ.refetch();
    },
  });

  const st = statusQ.data;
  const online = st?.online === true || (st as { health?: { ok?: boolean } })?.health?.ok === true;
  const label = useNodeDisplayLabel(node, t('common.localNode', 'Lokál'));
  const role = (st as { role?: string })?.role;
  const healthOk = (st as { health?: { ok?: boolean; status?: string } })?.health?.ok;
  const healthStatus = (st as { health?: { status?: string } })?.health?.status;

  return (
    <div className='phone-metro-stack'>
      <div className='phone-metro-picker'>
        <span className='phone-metro-picker-label'>{t('phone.node.target', 'Cíl')}</span>
        <NodePicker value={node} onChange={setNode} size='md' />
      </div>

      <div className='phone-metro-hero'>
        <div className='phone-metro-hero-top'>
          <StatusDot status={online ? 'running' : 'stopped'} />
          <span className='phone-metro-hero-title'>{label}</span>
          {role && <Badge variant={role === 'master' ? 'purple' : 'blue'}>{role}</Badge>}
          <Badge variant={online ? 'green' : 'red'} className='ml-auto'>
            {online ? 'online' : 'offline'}
          </Badge>
        </div>
        <div className='phone-metro-hero-sub'>
          health {healthOk ? 'ok' : (healthStatus ?? (statusQ.isLoading ? '…' : '—'))}
        </div>
      </div>

      <div className='phone-metro-actions'>
        <Button
          variant='secondary'
          size='sm'
          className='w-full'
          icon={<RefreshCw size={14} className={refreshMut.isPending ? 'animate-spin' : ''} />}
          disabled={refreshMut.isPending}
          onClick={() => refreshMut.mutate()}
        >
          {t('common.refresh', 'Obnovit')}
        </Button>
        <Button
          variant='primary'
          size='sm'
          className='w-full'
          icon={<Server size={14} />}
          onClick={() =>
            navigate(`/infra/nodes/${node ? encodeURIComponent(node) : 'local'}`)
          }
        >
          {t('phone.node.open', 'Workspace')}
        </Button>
      </div>

      <button type='button' className='phone-metro-row' onClick={() => navigate('/core/mesh')}>
        <Network size={18} strokeWidth={1.75} />
        <div className='phone-metro-row-text'>
          <span className='phone-metro-row-title'>
            {t('phone.node.fleet', 'Celá infrastruktura')}
          </span>
        </div>
        <ChevronRight size={16} className='text-gray-600 shrink-0' />
      </button>
    </div>
  );
}

function useStateNode(seed: string | null): [string | null, (n: string | null) => void] {
  const [node, setNode] = useState<string | null>(seed);
  useEffect(() => {
    setNode(seed);
  }, [seed]);
  return [node, setNode];
}

export const PHONE_WIDGETS: WidgetSpec[] = [
  {
    id: 'phone.dashboard',
    title: 'Přehled',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: LayoutDashboard,
    since: '2.1.0',
    component: PhoneDashboardBody,
    phone: PhoneDashboardBody,
  },
  {
    id: 'phone.codable',
    title: 'Codable',
    module: 'codable',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: CodableIcon,
    since: '2.1.0',
    component: PhoneCodableBody,
    phone: PhoneCodableBody,
  },
  {
    id: 'phone.node',
    title: 'Nod',
    module: 'mesh',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Server,
    since: '2.1.0',
    component: PhoneNodeBody,
    phone: PhoneNodeBody,
  },
];

/** @deprecated Start je jen menu; plochy jsou /phone/* */
export const PHONE_HOME: WidgetPanel = {
  id: 'phone-home',
  label: 'Start',
  node: null,
  widgets: [
    { widgetId: 'phone.dashboard', format: 'md' },
    { widgetId: 'phone.node', format: 'md' },
    { widgetId: 'phone.codable', format: 'md' },
  ],
};
